import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import Layout from "../component/layout";
import { toastify } from "../../../utils/toast";
import { makeRequest2 } from "../../../utils/makeRequest";
import { waterMeterBaseUrl, getFacilitiesURL, get_units_per_facility } from "../../../utils/urls";

const AddWaterMeter = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [serialNumber, setSerialNumber] = useState("");
  const [manufacturer, setManufacturer] = useState("");
  const [protocol, setProtocol] = useState("");
  const [size, setSize] = useState("");
  const [unitId, setUnitId] = useState("");
  const [facilityId, setFacilityId] = useState("");
  const [concentratorSerialNumber, setConcentratorSerialNumber] = useState("");
  const [bulkMeter, setBulkMeter] = useState(false);
  const [bulkMeterDescription, setBulkMeterDescription] = useState("");


  // Config states
  const [manufacturers, setManufacturers] = useState([]);
  const [protocols, setProtocols] = useState([]);
  const [meterSizes, setMeterSizes] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [facilityUnits, setFacilityUnits] = useState([]);

  // Base URLs for configs
  const waterMeterSizeBaseUrl = '/api/core/water-meters-sizes';
  const waterMeterProtocolsBaseUrl = '/api/core/water-meter-communcation-protocol';
  const waterMeterManufacturerBaseUrl = '/api/core/water-meter-manufacturer';
  const facilitiesBaseUrl = getFacilitiesURL;

  // Fetch all configuration data on component mount
  useEffect(() => {
    const fetchConfigs = async () => {
      try {
        setIsLoading(true);
        const [
          sizesRes,
          protocolsRes,
          manufacturersRes,
          facilitiesRes
        ] = await Promise.all([
          makeRequest2(waterMeterSizeBaseUrl, 'GET'),
          makeRequest2(waterMeterProtocolsBaseUrl, 'GET'),
          makeRequest2(waterMeterManufacturerBaseUrl, 'GET'),
          makeRequest2(facilitiesBaseUrl, 'GET')
        ]);

        if (sizesRes.success) setMeterSizes(sizesRes.data || []);
        if (protocolsRes.success) setProtocols(protocolsRes.data || []);
        if (manufacturersRes.success) setManufacturers(manufacturersRes.data || []);

        // Handle facilities response
        if (facilitiesRes.success) {
          let facilitiesData = [];

          if (Array.isArray(facilitiesRes.data)) {
            facilitiesData = facilitiesRes.data;
          } else if (facilitiesRes.data && Array.isArray(facilitiesRes.data.facilities)) {
            facilitiesData = facilitiesRes.data.facilities;
          } else if (facilitiesRes.facilities && Array.isArray(facilitiesRes.facilities)) {
            facilitiesData = facilitiesRes.facilities;
          }

          console.log("Facilities data:", facilitiesData);
          setFacilities(facilitiesData);
        }
      } catch (error) {
        console.error('Error fetching configurations:', error);
        toastify('Error loading configuration data', 'error');
      } finally {
        setIsLoading(false);
      }
    };

    fetchConfigs();
  }, []);

  // Fetch units for a specific facility
  const fetchFacilityUnits = async (facilityId) => {
    if (!facilityId) {
      setFacilityUnits([]);
      setUnitId("");
      return;
    }

    try {
      setIsLoading(true);
      const response = await makeRequest2(`${get_units_per_facility}/${facilityId}`, 'GET');

      if (response.success) {
        setFacilityUnits(response.data || []);
      } else {
        console.warn('Failed to fetch units');
        setFacilityUnits([]);
      }
    } catch (error) {
      console.error('Error fetching units:', error);
      toastify('Error loading units for this facility', 'error');
      setFacilityUnits([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle facility selection change
  const handleFacilityChange = (e) => {
    const selectedFacilityId = e.target.value;
    setFacilityId(selectedFacilityId);
    setUnitId(""); // Reset unit selection when facility changes
    fetchFacilityUnits(selectedFacilityId);
  };

  const handleSubmit = async () => {
    // Basic field validations
    if (!serialNumber.trim()) {
      toastify("Serial Number is required", "error");
      return;
    }
    if (!manufacturer) {
      toastify("Manufacturer is required", "error");
      return;
    }
    if (!protocol) {
      toastify("Protocol is required", "error");
      return;
    }
    if (!size) {
      toastify("Size is required", "error");
      return;
    }

    setIsSubmitting(true);

    const waterMeterData = {
      serialNumber: serialNumber.trim(),
      manufacturer,
      protocol,
      size,
      initialReading: 0.00,
      facilityId: facilityId || undefined,
      bulkMeter,
      bulkMeterDescription: bulkMeter ? bulkMeterDescription : undefined
    };

    if (!bulkMeter && unitId) {
      waterMeterData.unitId = unitId;
      waterMeterData.isInstalled = true;
    }
    if (concentratorSerialNumber) {
      waterMeterData.concentratorSerialNumber = concentratorSerialNumber;
    }

    try {
      const response = await makeRequest2(
        waterMeterBaseUrl,
        "POST",
        waterMeterData
      );

      if (response.success) {
        toastify("Water meter added successfully", "success");
        handleClear();
        navigate("/core/water_meter_management/meters");
      } else {
        toastify(response.error || "Failed to add water meter", "error");
      }
    } catch (error) {
      console.error("Error adding water meter:", error);
      toastify("An unexpected error occurred while adding the water meter", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClear = () => {
    setSerialNumber("");
    setManufacturer("");
    setProtocol("");
    setSize("");
    setFacilityId("");
    setUnitId("");
    setConcentratorSerialNumber("");
    setFacilityUnits([]);
  };

  if (isLoading && facilities.length === 0) {
    return (
      <Layout>
        <div className="text-center p-5">
          Loading configuration data...
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/core/water_meter_management/meters">Water Meters</Link>
                </li>
                <li className="breadcrumb-item">Add New Water Meter</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-header">
              <Link to="#" onClick={() => navigate(-1)}>
                <i className="ti ti-arrow-narrow-left"></i> Back
              </Link>
            </div>
          </div>
          <div className="card">
            <div className="card-header">
              <h5>Add New Water Meter</h5>
            </div>
            <div className="card-body">
              <div className="container">
                <div className="row">
                  {/* Serial Number */}
                  <div className="col-sm-6">
                    <label className="form-label">
                      Serial Number<span style={{ color: "red" }}>*</span>
                    </label>
                    <InputText
                      className="form-control"
                      value={serialNumber}
                      onChange={(e) => setSerialNumber(e.target.value)}
                      placeholder="Enter serial number"
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* Manufacturer */}
                  <div className="col-sm-6">
                    <label className="form-label">
                      Manufacturer<span style={{ color: "red" }}>*</span>
                    </label>
                    <select
                      className="form-control"
                      value={manufacturer}
                      onChange={(e) => setManufacturer(e.target.value)}
                      disabled={isSubmitting}
                    >
                      <option value="">Select Manufacturer</option>
                      {manufacturers.map((mfr) => (
                        <option key={mfr._id} value={mfr.name}>
                          {mfr.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Protocol */}
                  <div className="col-sm-6">
                    <label className="form-label">
                      Protocol<span style={{ color: "red" }}>*</span>
                    </label>
                    <select
                      className="form-control"
                      value={protocol}
                      onChange={(e) => setProtocol(e.target.value)}
                      disabled={isSubmitting}
                    >
                      <option value="">Select Protocol</option>
                      {protocols.map((proto) => (
                        <option key={proto._id} value={proto.name}>
                          {proto.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Size */}
                  <div className="col-sm-6">
                    <label className="form-label">
                      Size<span style={{ color: "red" }}>*</span>
                    </label>
                    <select
                      className="form-control"
                      value={size}
                      onChange={(e) => setSize(e.target.value)}
                      disabled={isSubmitting}
                    >
                      <option value="">Select Size</option>
                      {meterSizes.map((s) => (
                        <option key={s._id} value={s.code}>
                          {s.code}
                        </option>
                      ))}
                    </select>

                    {/* Bulk Meter Checkbox */}
                    <div className="col-sm-6">
                      <label className="form-label">Bulk Meter?</label>
                      <div>
                        <input
                          type="checkbox"
                          checked={bulkMeter}
                          onChange={(e) => {
                            setBulkMeter(e.target.checked);
                            if (!e.target.checked) {
                              setBulkMeterDescription("");
                            }
                          }}
                          disabled={isSubmitting}
                        />{" "}
                        Yes
                      </div>
                    </div>

                    {/* Bulk Meter Description Dropdown (only if bulk meter is checked) */}
                    {bulkMeter && (
                      <div className="col-sm-6">
                        <label className="form-label">
                          Bulk Meter Description<span style={{ color: "red" }}>*</span>
                        </label>
                        <select
                          className="form-control"
                          value={bulkMeterDescription}
                          onChange={(e) => setBulkMeterDescription(e.target.value)}
                          disabled={isSubmitting}
                        >
                          <option value="">Select Description</option>
                          <option value="City Council Bulk">City Council Bulk</option>
                          <option value="Borehole Bulk">Borehole Bulk</option>
                          <option value="Bulk Borehole Outlet">Bulk Borehole Outlet</option>
                          <option value="Bulk Inlet">Bulk Inlet</option>
                          <option value="Bulk Outlet">Bulk Outlet</option>
                        </select>
                      </div>
                    )}

                  </div>

                  {/* Facility Dropdown */}
                  <div className="col-sm-6">
                    <label className="form-label">
                      Facility (Optional)
                    </label>
                    <select
                      className="form-control"
                      value={facilityId}
                      onChange={handleFacilityChange}
                      disabled={isSubmitting}
                    >
                      <option value="">Select Facility</option>
                      {facilities.map((facility) => (
                        <option key={facility._id} value={facility._id}>
                          {facility.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Unit Dropdown */}
                  {!bulkMeter && (
                    <div className="col-sm-6">
                      <label className="form-label">Unit (Optional)</label>
                      <select
                        className="form-control"
                        value={unitId}
                        onChange={(e) => setUnitId(e.target.value)}
                        disabled={isSubmitting || facilityUnits.length === 0}
                      >
                        <option value="">Select Unit</option>
                        {facilityUnits.map((unit) => (
                          <option key={unit._id} value={unit._id}>
                            {unit.name}
                          </option>
                        ))}
                      </select>
                      {facilityId && facilityUnits.length === 0 && !isLoading && (
                        <small className="text-muted">No units found for this facility</small>
                      )}
                      {!facilityId && (
                        <small className="text-muted">
                          Please select a facility first to load available units
                        </small>
                      )}
                    </div>
                  )}

                  {/* Concentrator Serial Number - replacing the dropdown with text input */}
                  <div className="col-sm-6">
                    <label className="form-label">
                      Concentrator Serial Number (Optional)
                    </label>
                    <InputText
                      className="form-control"
                      value={concentratorSerialNumber}
                      onChange={(e) => setConcentratorSerialNumber(e.target.value)}
                      placeholder="Enter concentrator serial number"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              </div>

              <div className="text-end mt-4">
                <Button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={handleClear}
                  disabled={isSubmitting}
                >
                  Clear
                </Button>
                &nbsp;
                <Button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Submitting..." : "Submit"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AddWaterMeter;